import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';
import AnimateHeight from 'react-animate-height';

import {
  HOME,
  PROFILE,
  BILLING,
  DEVICES,
  SUPPORT,
  SHOP,
} from '../../../routes';
import LogoIcon from '../../../assets/icons/sidebar/LogoIcon';
import HomeIcon from '../../../assets/icons/sidebar/HomeIcon';
import UserIcon from '../../../assets/icons/sidebar/UserIcon';
import BillingIcon from '../../../assets/icons/sidebar/BillingIcon';
import DevicesIcon from '../../../assets/icons/sidebar/DevicesIcon';
import ShopIcon from '../../../assets/icons/sidebar/ShopIcon';
import SupportIcon from '../../../assets/icons/sidebar/SupportIcon';
import { useGetCustomerQuery } from '../../../redux/customersApi';
import MenuBarsIcon from '../../../assets/icons/common/MenuBarsIcon';
import CloseIcon from '../../../assets/icons/common/CloseIcon';
import { FEEDBACK_JOTFORM_LINK } from '../../../constants/links';
import FeedbackIcon from '../../../assets/icons/sidebar/FeedbackIcon';

import styles from './styles.module.scss';

const menuLinks = [
  {
    title: 'Home',
    link: HOME,
    icon: <HomeIcon />,
    isExternal: false,
  },
  {
    title: 'Billing & Cards',
    link: BILLING,
    icon: <BillingIcon />,
    isExternal: false,
  },
  {
    title: 'Devices & Plans',
    link: DEVICES,
    icon: <DevicesIcon />,
    isExternal: false,
  },
  {
    title: 'Shop',
    link: SHOP,
    icon: <ShopIcon />,
    isExternal: true,
  },
  {
    title: 'Support',
    link: SUPPORT,
    icon: <SupportIcon />,
    isExternal: false,
  },
  {
    title: 'Leave Us Feedback!',
    link: FEEDBACK_JOTFORM_LINK,
    icon: <FeedbackIcon className={styles.feedbackIcon} />,
    isExternal: true,
  },
];

const MenuLink = ({ children, isExternal, link, className }) => {
  if (isExternal) {
    return (
      <a
        className={className}
        href={link}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  } else {
    return (
      <Link className={className} to={link}>
        {children}
      </Link>
    );
  }
};

const Sidebar = ({ isMobile }) => {
  const { data: customer } = useGetCustomerQuery();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const toggleMenu = () => setOpen((value) => !value);

  const isActiveLink = (link) => {
    return link === location.pathname;
  };

  return (
    <div className={styles.sidebar}>
      <div className={cn({ [styles.logoWrapper]: true, [styles.open]: open })}>
        <Link to={HOME}>
          <LogoIcon className={styles.logo} />
        </Link>
        <button className={styles.button} onClick={toggleMenu}>
          <MenuBarsIcon
            className={cn({ [styles.menuBars]: true, [styles.active]: !open })}
          />
          <CloseIcon
            className={cn({ [styles.menuClose]: true, [styles.active]: open })}
          />
        </button>
      </div>
      <AnimateHeight duration={200} height={open || !isMobile ? 'auto' : 0}>
        <div
          className={cn(styles.profileWrapper, {
            [styles.active]: isActiveLink(PROFILE),
          })}
        >
          <Link className={styles.profileLink} to={PROFILE}>
            <span className={styles.profileCircle}>
              <UserIcon />
            </span>
            {customer?.first_name} {customer?.last_name}
          </Link>
        </div>
        <ul className={styles.menu}>
          {menuLinks.map(({ title, link, icon, isExternal }) => (
            <li className={styles.menuItem} key={title}>
              <MenuLink
                className={cn(styles.menuLink, {
                  [styles.active]: isActiveLink(link),
                })}
                link={link}
                isExternal={isExternal}
              >
                <span className={styles.menuIconWrapper}>{icon}</span>
                <span className={styles.menuText}>{title}</span>
              </MenuLink>
            </li>
          ))}
        </ul>
      </AnimateHeight>
      {isMobile && <div className={styles.mobilePlank}></div>}
    </div>
  );
};

export default Sidebar;
